import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useAuth } from '@rbac/useCan';
import { setActiveCompanyId } from '@/lib/api';

/**
 * The active company for every authenticated screen — what the top-right switcher sets
 * and what client/src/lib/api.js sends as X-Company-Id on every request.
 *
 * This is a *preference*, never an authorization: the server validates the id against the
 * user's memberships in services/rbac/identity.js and silently ignores anything they are
 * not entitled to. Storing it in localStorage is therefore safe — the worst a tampered
 * value achieves is being ignored.
 *
 * Mirrors ChatDockContext (the app's only other context) and mounts in DefaultLayout, the
 * one place both the Navbar and the routed pages are descendants.
 */
const CompanyContext = createContext(null);

const STORAGE_KEY = 'app:company';

function readStored() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const n = Number(raw);
    return raw && Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
}

export function CompanyProvider({ children }) {
  const user = useAuth();
  const companies = useMemo(() => (user && user.companies) || [], [user]);
  const [companyId, setCompanyId] = useState(readStored);

  // Drop a stored company that is no longer offered (membership revoked, or a different
  // user logged in on this browser) — otherwise the header keeps naming a company the
  // server will ignore, and the Select renders a blank value.
  useEffect(() => {
    if (!companies.length) return;
    const known = companies.some((c) => Number(c.id) === Number(companyId));
    if (!known) {
      const fallback = user && user.companyId != null ? Number(user.companyId) : Number(companies[0].id);
      setCompanyId(fallback);
    }
  }, [companies, companyId, user]);

  // Publish to the axios interceptor BEFORE any child effect can fire a request, so the
  // first fetch after a switch already carries the new company.
  setActiveCompanyId(companyId);

  useEffect(() => {
    try {
      if (companyId == null) localStorage.removeItem(STORAGE_KEY);
      else localStorage.setItem(STORAGE_KEY, String(companyId));
    } catch { /* private mode — the header just falls back to the user's default */ }
  }, [companyId]);

  const setCompany = useCallback((next) => setCompanyId(next == null ? null : Number(next)), []);

  const value = useMemo(
    () => ({ companyId, companies, setCompany }),
    [companyId, companies, setCompany],
  );
  return <CompanyContext.Provider value={value}>{children}</CompanyContext.Provider>;
}

export function useCompany() {
  return useContext(CompanyContext) || { companyId: null, companies: [], setCompany: () => {} };
}
